import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import AnalyticsTracker from "@/lib/analytics";
import AnalyticsConsentBanner from "@/components/AnalyticsConsentBanner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { CountryProvider } from "@/context/CountryContext";
import { AmbientSoundProvider } from "@/context/AmbientSoundContext";
import AmbientSoundWidget from "@/components/AmbientSoundWidget";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import HomeBooking from "@/pages/HomeBooking";
import ExpertDetailBooking from "@/pages/ExpertDetailBooking";
import DossierWizard from "@/pages/DossierWizard";
import DossierAuditSuccess from "@/pages/DossierAuditSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Security from "@/pages/Security";
import Verifier from "@/pages/Verifier";
import TrouverExpertPage from "@/pages/TrouverExpertPage";
import VerifierVerticalPage from "@/pages/VerifierVerticalPage";
import Contact from "@/pages/Contact";
import SpecialtyPage from "@/pages/SpecialtyPage";
import VerifyProfessionalPage from "@/pages/VerifyProfessionalPage";
import ProblematiquePage from "@/pages/ProblematiquePage";

// Wrappers inline pour passer le slug en prop
const SpecialtyPageLazy = ({ slug }) => <SpecialtyPage slug={slug} />;
const VerifyProPageLazy = ({ slug }) => <VerifyProfessionalPage slug={slug} />;
const ProblematiquePageLazy = ({ slug }) => <ProblematiquePage slug={slug} />;
import MissionNew from "@/pages/MissionNew";
import Dashboard from "@/pages/Dashboard";
import ExpertDashboard from "@/pages/ExpertDashboard";
import ExpertPhotoStudio from "@/pages/ExpertPhotoStudio";
import ExpertNetwork from "@/pages/ExpertNetwork";
import Negociation from "@/pages/Negociation";
import NegociationNouveau from "@/pages/NegociationNouveau";
import NegociationDossier from "@/pages/NegociationDossier";
import NegociationDossiers from "@/pages/NegociationDossiers";
import NegociationInvitation from "@/pages/NegociationInvitation";
import SpecialiteCorporateMA from "@/pages/SpecialiteCorporateMA";
import ExpertApply from "@/pages/ExpertApply";
import AdminDashboard from "@/pages/AdminDashboard";
import Messages from "@/pages/Messages";
import Legal from "@/pages/Legal";
import CodeConformite from "@/pages/CodeConformite";
import ReseauSpecialistes from "@/pages/ReseauSpecialistes";
import Pricing from "@/pages/Pricing";
import PaymentReturn from "@/pages/PaymentReturn";
import About from "@/pages/About";
import ExternalResearch from "@/pages/ExternalResearch";
import Insights from "@/pages/Insights";
import InsightArticle from "@/pages/InsightArticle";
import SpecialistesProfession from "@/pages/SpecialistesProfession";
import ArticleEditor from "@/pages/ArticleEditor";
import Galerie from "@/pages/Galerie";
import Rejoindre from "@/pages/Rejoindre";
import Claim from "@/pages/Claim";
import ClaimProfile from "@/pages/ClaimProfile";
import Workspace from "@/pages/Workspace";
// Videos page retirée temporairement (sur demande utilisateur)
import MyVoiceCalls from "@/pages/MyVoiceCalls";
import RdvRoom from "@/pages/RdvRoom";
import { ExpertisesIndex, VerticalLanding, ProfessionLanding } from "@/pages/Expertises";
import ProtectedRoute from "@/components/ProtectedRoute";
import FloatingVoiceButton from "@/components/FloatingVoiceButton";
import PageTransition from "@/components/PageTransition";
import AdminCockpit from "@/pages/AdminCockpit";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminPendingExperts from "@/pages/AdminPendingExperts";
import AdminExpertsManage from "@/pages/AdminExpertsManage";
import AdminSupervision from "@/pages/AdminSupervision";
import AdminSeoWriter from "@/pages/AdminSeoWriter";
import InsightsCategory from "@/pages/InsightsCategory";
import MatchDemo from "@/pages/MatchDemo";
import ExpertRdvDashboard from "@/pages/ExpertRdvDashboard";
import AdminDossiersPending from "@/pages/AdminDossiersPending";
import AdminTwilio from "@/pages/AdminTwilio";
import AdminAgentsIA from "@/pages/AdminAgentsIA";
import MaintenanceGate from "@/components/MaintenanceGate";
import NotFound from "@/pages/NotFound";

const ROLES_ALL = ["client", "expert", "admin"];
const ROLES_EXPERT = ["expert", "admin"];
const ROLES_ADMIN = ["admin"];

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <CountryProvider>
          <AmbientSoundProvider>
            <BrowserRouter>
              <AnalyticsTracker />
              <AnalyticsConsentBanner />
              <MaintenanceGate>
              <PageTransition>
              <Routes>
                <Route path="/" element={<HomeBooking />} />
                {/* Anciennes portes d'entrée redondantes → toutes réunifiées vers /dossier (parcours unique CONFILEX). */}
                <Route path="/accueil-classique" element={<Navigate to="/" replace />} />
                <Route path="/recherche" element={<Navigate to="/dossier" replace />} />
                <Route path="/orientation" element={<Navigate to="/dossier" replace />} />
                <Route path="/orientation-classique" element={<Navigate to="/dossier" replace />} />
                <Route path="/orientation-intelligente" element={<Navigate to="/dossier" replace />} />
                <Route path="/orientation/intelligente" element={<Navigate to="/dossier" replace />} />
                <Route path="/concierge" element={<Navigate to="/dossier" replace />} />
                <Route path="/audit/transverse" element={<Navigate to="/dossier" replace />} />
                <Route path="/verificateur" element={<Navigate to="/verifier" replace />} />
                {/* Réseau de spécialistes publics — parcours dossier only (l'utilisateur ne choisit pas dans une liste) */}
                <Route path="/experts" element={<Navigate to="/dossier" replace />} />
                <Route path="/experts/:id" element={<Navigate to="/dossier" replace />} />
                <Route path="/our-experts" element={<Navigate to="/dossier" replace />} />
                <Route path="/notre-reseau" element={<Navigate to="/dossier" replace />} />
                {/* Anciennes URLs "annuaire-*" → redirection 301 vers "reseau-specialistes" (SEO) */}
                <Route path="/annuaire-avocats" element={<Navigate to="/reseau-specialistes" replace />} />
                <Route path="/annuaire-avocats/:slug" element={<Navigate to="/reseau-specialistes" replace />} />
                <Route path="/reseau-specialistes" element={<ReseauSpecialistes />} />
                <Route path="/reseau-specialistes/:slug" element={<ReseauSpecialistes />} />
                {/* Pages actives */}
                <Route path="/cabinet/:slug" element={<ExpertDetailBooking />} />
                <Route path="/dossier" element={<DossierWizard />} />
                <Route path="/dossier/:caseId/audit/success" element={<DossierAuditSuccess />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/security" element={<Security />} />
                <Route path="/verifier" element={<Verifier />} />

                {/* Pages SEO — Trouver un X (5 professions + Réseau de spécialistes) */}
                <Route path="/trouver-un-avocat" element={<TrouverExpertPage slug="trouver-un-avocat" />} />
                <Route path="/trouver-un-avocat/:city" element={<TrouverExpertPage slug="trouver-un-avocat" />} />
                <Route path="/trouver-un-expert-comptable" element={<TrouverExpertPage slug="trouver-un-expert-comptable" />} />
                <Route path="/trouver-un-expert-comptable/:city" element={<TrouverExpertPage slug="trouver-un-expert-comptable" />} />
                <Route path="/trouver-un-notaire" element={<TrouverExpertPage slug="trouver-un-notaire" />} />
                <Route path="/trouver-un-notaire/:city" element={<TrouverExpertPage slug="trouver-un-notaire" />} />
                <Route path="/trouver-un-mediateur" element={<TrouverExpertPage slug="trouver-un-mediateur" />} />
                <Route path="/trouver-un-commissaire-de-justice" element={<TrouverExpertPage slug="trouver-un-commissaire-de-justice" />} />
                <Route path="/annuaire-experts" element={<TrouverExpertPage slug="annuaire-experts" />} />
                <Route path="/annuaire-experts/:city" element={<TrouverExpertPage slug="annuaire-experts" />} />

                {/* Pages SEO — Univers Vérification */}
                <Route path="/verifier-un-contrat" element={<VerifierVerticalPage slug="verifier-un-contrat" />} />
                <Route path="/verifier-un-bail" element={<VerifierVerticalPage slug="verifier-un-bail" />} />
                <Route path="/verifier-une-facture" element={<VerifierVerticalPage slug="verifier-une-facture" />} />
                <Route path="/verifier-un-devis" element={<VerifierVerticalPage slug="verifier-un-devis" />} />
                <Route path="/verifier-documents-entreprise" element={<VerifierVerticalPage slug="verifier-documents-entreprise" />} />

                {/* Contact institutionnel */}
                <Route path="/contact" element={<Contact />} />

                {/* Pages SEO — Spécialités par domaine (24 landings) */}
                {[
                  "avocat-droit-du-travail", "avocat-immobilier", "avocat-fiscal",
                  "avocat-societes", "avocat-commercial", "avocat-famille",
                  "avocat-recouvrement", "avocat-affaires",
                  "expert-comptable-tpe", "expert-comptable-pme", "expert-comptable-sci",
                  "expert-comptable-sas", "expert-comptable-immobilier",
                  "expert-comptable-holding", "expert-comptable-startup", "expert-comptable-lmnp",
                  "notaire-achat-immobilier", "notaire-succession", "notaire-donation",
                  "notaire-investissement", "notaire-transmission", "notaire-patrimoine",
                  "negociation-amiable", "negociation-creances",
                ].map((slug) => (
                  <Route key={slug} path={`/${slug}`} element={<SpecialtyPageLazy slug={slug} />} />
                ))}

                {/* PILIER 4 — Vérifier un professionnel (6 landings stratégiques) */}
                {[
                  "verifier-un-avocat", "verifier-un-expert-comptable",
                  "verifier-un-notaire", "verifier-un-commissaire-de-justice",
                  "verifier-une-societe", "verifier-un-professionnel",
                ].map((slug) => (
                  <Route key={slug} path={`/${slug}`} element={<VerifyProPageLazy slug={slug} />} />
                ))}

                {/* PILIER 2 — Problématiques (18 landings haute intention) */}
                {[
                  // Entreprises
                  "controle-urssaf", "controle-fiscal", "contentieux-commercial",
                  "facture-impayee", "recouvrement", "pacte-associes",
                  "creation-sas", "creation-holding", "transmission-entreprise",
                  "audit-juridique", "audit-comptable",
                  // Particuliers
                  "succession", "divorce", "licenciement", "bail-locatif",
                  "copropriete", "fiscalite-personnelle", "donation",
                ].map((slug) => (
                  <Route key={slug} path={`/${slug}`} element={<ProblematiquePageLazy slug={slug} />} />
                ))}
                <Route path="/legal" element={<Legal />} />
                <Route path="/code-conformite" element={<CodeConformite />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/galerie" element={<Galerie />} />
                <Route path="/videos" element={<Navigate to="/" replace />} />
                <Route path="/rejoindre" element={<Rejoindre />} />
                <Route path="/claim/:token" element={<Claim />} />
                <Route path="/claim-profile/:token" element={<ClaimProfile />} />
                <Route path="/d/:accessToken" element={<Workspace />} />
                <Route path="/mes-appels" element={<MyVoiceCalls />} />
                <Route path="/mes-appels/:callId" element={<MyVoiceCalls />} />
                <Route path="/external-research/:id" element={<ExternalResearch />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/insights/categorie/:slug" element={<InsightsCategory />} />
                <Route path="/insights/:slug" element={<InsightArticle />} />
                <Route path="/specialistes/:profession" element={<SpecialistesProfession />} />
                <Route path="/specialistes/:profession/:city" element={<SpecialistesProfession />} />
                <Route path="/expertises" element={<ExpertisesIndex />} />
                <Route path="/expertises/:verticalId" element={<VerticalLanding />} />
                <Route path="/expertises/:verticalId/:professionId" element={<ProfessionLanding />} />
                <Route path="/specialites/corporate-m-a" element={<SpecialiteCorporateMA />} />
                <Route path="/rdv/:accessToken" element={<RdvRoom />} />
                <Route path="/payment/return" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />

                <Route path="/missions/new" element={<ProtectedRoute><MissionNew /></ProtectedRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute roles={ROLES_ALL}><Dashboard /></ProtectedRoute>} />
                <Route path="/expert/dashboard" element={<ProtectedRoute roles={ROLES_EXPERT}><ExpertDashboard /></ProtectedRoute>} />
                <Route path="/expert/photo" element={<ProtectedRoute roles={ROLES_EXPERT}><ExpertPhotoStudio /></ProtectedRoute>} />
                <Route path="/expert/network" element={<ProtectedRoute roles={ROLES_EXPERT}><ExpertNetwork /></ProtectedRoute>} />

                {/* CONFILEX Négociation — structuration documentaire des échanges amiables */}
                <Route path="/negociation" element={<Negociation />} />
                <Route path="/negociation/nouveau" element={<ProtectedRoute roles={ROLES_ALL}><NegociationNouveau /></ProtectedRoute>} />
                <Route path="/negociation/mes-dossiers" element={<ProtectedRoute roles={ROLES_ALL}><NegociationDossiers /></ProtectedRoute>} />
                <Route path="/negociation/dossier/:id" element={<ProtectedRoute roles={ROLES_ALL}><NegociationDossier /></ProtectedRoute>} />
                <Route path="/negociation/invitation/:token" element={<NegociationInvitation />} />
                <Route path="/expert/dashboard/rdv" element={<ProtectedRoute roles={ROLES_EXPERT}><ExpertRdvDashboard /></ProtectedRoute>} />
                <Route path="/expert/apply" element={<ProtectedRoute><ExpertApply /></ProtectedRoute>} />
                <Route path="/expert/articles/new" element={<ProtectedRoute roles={ROLES_EXPERT}><ArticleEditor /></ProtectedRoute>} />
                <Route path="/expert/articles/:id" element={<ProtectedRoute roles={ROLES_EXPERT}><ArticleEditor /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/analytics" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminAnalytics /></ProtectedRoute>} />
                <Route path="/admin/applications" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminPendingExperts /></ProtectedRoute>} />
                <Route path="/admin/experts/manage" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminExpertsManage /></ProtectedRoute>} />
                <Route path="/admin/cockpit" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminCockpit /></ProtectedRoute>} />
                <Route path="/admin/supervision" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminSupervision /></ProtectedRoute>} />
                <Route path="/admin/seo-writer" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminSeoWriter /></ProtectedRoute>} />
                <Route path="/demo/match" element={<MatchDemo />} />
                <Route path="/admin/dossiers-en-attente" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminDossiersPending /></ProtectedRoute>} />
                <Route path="/admin/twilio" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminTwilio /></ProtectedRoute>} />
                <Route path="/admin/agents-ia" element={<ProtectedRoute roles={ROLES_ADMIN}><AdminAgentsIA /></ProtectedRoute>} />
                <Route path="/messages/:missionId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </PageTransition>
              </MaintenanceGate>
              <AmbientSoundWidget />
              <FloatingVoiceButton />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </AmbientSoundProvider>
          </CountryProvider>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
